"use client";

import { FC, memo, SyntheticEvent, useCallback, useContext } from "react";
import { ITournamentFeedResponse } from "@finbackoffice/websocket-client";
import ActiveLink from "components/base/active-link/ActiveLink";
import { RouteNames } from "utils/constants";
import classNames from "classnames";
import { PrematchContext } from "contexts";
import { useMultiSelectPrematchCurrentTournamentId } from "hooks";
import { useRouter } from "next/navigation";
import styles from "./prematch-all-tournament.module.sass";

export interface IProps {
    tournament: ITournamentFeedResponse;
    sportSlug: string;
    sportId: number;
    categoryId: number;
    showCheckbox: boolean;
    isSelected: boolean;
}

const PrematchAllTournament: FC<IProps> = ({
    tournament,
    categoryId,
    sportSlug,
    showCheckbox,
    sportId,
    isSelected,
}) => {
    const router = useRouter();
    const { addTournament, removeTournament, changeTournament, tournaments } =
        useContext(PrematchContext);
    const selectedTournamentId = useMultiSelectPrematchCurrentTournamentId();

    const onCheckBoxClickHandler = useCallback(
        (event: SyntheticEvent) => {
            event.stopPropagation();
            event.preventDefault();

            if (isSelected) {
                removeTournament(tournament?.id);
            } else if (sportId) {
                addTournament({
                    sportId,
                    categoryId,
                    tournamentId: tournament?.id,
                });

                if (!tournaments.length) {
                    router.push(
                        `/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${sportSlug}/${categoryId}/${tournament.id}`,
                    );
                }
            }
        },
        [
            addTournament,
            categoryId,
            isSelected,
            removeTournament,
            router,
            sportId,
            sportSlug,
            tournament.id,
            tournaments.length,
        ],
    );

    const onItemClickHandler = useCallback(() => {
        if (sportId) {
            if (tournament.id !== selectedTournamentId) {
                changeTournament({
                    sportId,
                    categoryId,
                    tournamentId: tournament?.id,
                });
            }
        }
    }, [sportId, tournament.id, selectedTournamentId, changeTournament, categoryId]);

    return (
        <ActiveLink
            href={{
                pathname: `/${RouteNames.SPORT}/${RouteNames.PREMATCH}/${sportSlug}/${categoryId}/${tournament.id}`,
            }}
            forceActive={isSelected}
            onClick={onItemClickHandler}
            activeClassName={styles.active}
            className={styles.tournamentItem}
            data-testid="sportsbook-prematch-tournament-link">
            <>
                {showCheckbox && (
                    <label
                        htmlFor="checkbox"
                        className={classNames("ui-checkbox", isSelected && "checked")}>
                        <input type="checkbox" id="checkbox" />
                        <i onClick={onCheckBoxClickHandler} />
                    </label>
                )}
                <span>{tournament.name}</span>
                <span>{tournament.games_count}</span>
            </>
        </ActiveLink>
    );
};

export default memo(PrematchAllTournament);
